/**
 * Tab strip switching between the two marketplace analytics surfaces:
 *   - "Analytics"  → /admin/marketplace-analytics (default, all roles)
 *   - "Backfill"   → /admin/marketplace-backfill  (SuperAdmin-only)
 *
 * Backfill is hidden for non-SuperAdmin since the backend route is SA-only.
 * Rendered just below the PageHeader on both pages so users can pivot
 * between them without going back to the global nav.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { useAuth } from '../../context/AuthContext';

type ActiveTab = 'analytics' | 'backfill';

interface Props {
  active: ActiveTab;
}

const ROUTES: Record<ActiveTab, string> = {
  analytics: '/admin/marketplace-analytics',
  backfill: '/admin/marketplace-backfill',
};

const MarketplaceAnalyticsTabs: React.FC<Props> = ({ active }) => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  // For non-SA, only the Analytics tab is reachable — render nothing rather
  // than a one-item strip.
  if (!isSuperAdmin) return null;

  const renderTab = (key: ActiveTab, label: string) => {
    const isActive = active === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => {
          if (!isActive) navigate(ROUTES[key]);
        }}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
          isActive
            ? 'bg-white text-gray-950 shadow-sm dark:bg-gray-950 dark:text-gray-50'
            : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-50',
        )}
        aria-current={isActive ? 'page' : undefined}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1 dark:bg-gray-800">
      {renderTab('analytics', 'Analytics')}
      {renderTab('backfill', 'Backfill')}
    </div>
  );
};

export default MarketplaceAnalyticsTabs;
